import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api, errMsg, BOAT_CATEGORIES, REGATA_TYPES, slug } from "@/lib/api";

const EMPTY = {
  denominazione: "", data_inizio: "", data_fine: "", data_termine_iscrizioni: "", luogo: "", club_numero: "", club_nome: "",
  club_indirizzo: "", tipo: "", categorie_barca: [], nota: "", link_iscrizioni: "", link_albo: "",
};

const Field = ({ label, children }) => (
  <div><Label className="mb-1 block">{label}</Label>{children}</div>
);

export const RegataForm = ({ open, onOpenChange, regata, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [fromArchive, setFromArchive] = useState(false);

  useEffect(() => {
    if (open) {
      setF(regata ? { ...EMPTY, ...regata } : EMPTY);
      setFromArchive(false);
    }
  }, [open, regata]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setInizio = (e) => {
    const v = e.target.value;
    setF((x) => ({ ...x, data_inizio: v, data_fine: !x.data_fine || x.data_fine < v ? v : x.data_fine }));
  };
  const lookup = async () => {
    const n = f.club_numero.trim();
    if (!n) return;
    try {
      const { data } = await api.get(`/circoli/${encodeURIComponent(n)}`);
      setF((x) => ({ ...x, club_numero: data.numero, club_nome: data.nome, luogo: x.luogo || data.citta || "" }));
      setFromArchive(true);
      toast.success(`Circolo trovato in archivio: ${data.nome}`);
    } catch {
      /* nuovo circolo: verrà aggiunto all'archivio */
    }
  };
  const toggleCat = (c) =>
    setF({ ...f, categorie_barca: f.categorie_barca.includes(c) ? f.categorie_barca.filter((x) => x !== c) : [...f.categorie_barca, c] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.tipo) return toast.error("Seleziona il tipo di regata");
    if (!f.categorie_barca.length) return toast.error("Seleziona almeno una categoria barca");
    setSaving(true);
    const { id, created_by, created_at, ...payload } = f;
    try {
      if (regata) await api.put(`/regate/${regata.id}`, payload);
      else await api.post("/regate", payload);
      toast.success(regata ? "Regata aggiornata" : "Regata inserita");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="regata-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl uppercase">{regata ? "Modifica regata" : "Nuova regata"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Denominazione regata"><Input required value={f.denominazione} onChange={set("denominazione")} placeholder="Es. Campionato Italiano Giovanile" data-testid="regata-name-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Data inizio"><Input type="date" required value={f.data_inizio} onChange={setInizio} data-testid="regata-start-input" /></Field>
            <Field label="Data fine"><Input type="date" required min={f.data_inizio || undefined} value={f.data_fine} onChange={set("data_fine")} data-testid="regata-end-input" /></Field>
            <Field label="Termine iscrizioni"><Input type="date" value={f.data_termine_iscrizioni} onChange={set("data_termine_iscrizioni")} data-testid="regata-deadline-input" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Tipo regata">
              <Select value={f.tipo} onValueChange={(v) => setF({ ...f, tipo: v })}>
                <SelectTrigger data-testid="regata-type-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{REGATA_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`regata-type-option-${slug(t)}`}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div>
            <Label className="mb-1 block font-semibold">Luogo</Label>
            <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
              <div className="sm:col-span-1"><Field label="N. circolo"><Input value={f.club_numero} onChange={(e) => { if (fromArchive) { setFromArchive(false); setF({ ...f, club_numero: e.target.value, club_nome: "" }); } else set("club_numero")(e); }} onBlur={lookup} data-testid="regata-club-number-input" /></Field></div>
              <div className="sm:col-span-3"><Field label="Nome circolo"><Input required value={f.club_nome} readOnly={fromArchive} className={fromArchive ? "bg-slate-50" : ""} onChange={set("club_nome")} data-testid="regata-club-name-input" /></Field></div>
              <div className="sm:col-span-2"><Field label="Città"><Input required value={f.luogo} onChange={set("luogo")} data-testid="regata-city-input" /></Field></div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Se il numero circolo è già in archivio, il nome viene recuperato automaticamente.</p>
          </div>
          <Field label="Indirizzo circolo"><Input value={f.club_indirizzo} onChange={set("club_indirizzo")} data-testid="regata-club-address-input" /></Field>
          <Field label="Categorie barca">
            <div className="flex flex-wrap gap-4 pt-1">
              {BOAT_CATEGORIES.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={f.categorie_barca.includes(c)} onCheckedChange={() => toggleCat(c)} data-testid={`regata-category-${slug(c)}-checkbox`} />
                  {c}
                </label>
              ))}
            </div>
          </Field>
          <Field label="Nota"><Textarea rows={3} value={f.nota} onChange={set("nota")} data-testid="regata-note-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Link pagina iscrizioni"><Input type="url" placeholder="https://" value={f.link_iscrizioni} onChange={set("link_iscrizioni")} data-testid="regata-signup-link-input" /></Field>
            <Field label="Link Albo regata"><Input type="url" placeholder="https://" value={f.link_albo} onChange={set("link_albo")} data-testid="regata-board-link-input" /></Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="regata-form-cancel-button">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="regata-form-submit-button">
              {saving ? "Salvataggio…" : "Salva regata"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
